import { useState } from 'react';
import SectionHeading from '@/components/base/SectionHeading';
import Reveal from '@/components/base/Reveal';
import { accent, type AccentKey } from './theme';

export interface ScenarioItem {
  id: string;
  icon: string;
  title: string;
  desc: string;
  image: string;
  alt: string;
}

interface SolutionScenariosProps {
  eyebrow: string;
  title: string;
  scenarios: ScenarioItem[];
  variant: 'split' | 'tabs';
  accentKey: AccentKey;
}

export default function SolutionScenarios({
  eyebrow,
  title,
  scenarios,
  variant,
  accentKey,
}: SolutionScenariosProps) {
  const a = accent(accentKey);
  const [active, setActive] = useState(0);

  return (
    <section id="scenarios" className="w-full px-4 md:px-6 py-16 md:py-24 bg-background-100 scroll-mt-20">
      <div className="max-w-[1200px] mx-auto">
        <SectionHeading eyebrow={eyebrow} title={title} />

        {variant === 'split' ? (
          <div className="mt-12 flex flex-col gap-12">
            {scenarios.map((scenario, idx) => (
              <Reveal key={scenario.id}>
                <div
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center ${
                    idx % 2 === 1 ? 'lg:[&>*:first-child]:order-2' : ''
                  }`}
                >
                  <div className="relative rounded-lg overflow-hidden">
                    <img
                      src={scenario.image}
                      alt={scenario.alt}
                      title={scenario.alt}
                      className="w-full h-[240px] md:h-[320px] object-cover"
                    />
                    <div className="absolute top-4 left-4 w-10 h-10 flex items-center justify-center rounded-full bg-foreground-950/70 backdrop-blur text-background-50">
                      <i className={`${scenario.icon} text-lg`}></i>
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center gap-3">
                      <span className={`text-sm font-bold ${a.text}`}>场景 {String(idx + 1).padStart(2, '0')}</span>
                      <span className="h-px flex-1 bg-background-300"></span>
                    </div>
                    <h3 className="mt-3 font-heading font-bold text-foreground-950 text-2xl leading-snug">
                      {scenario.title}
                    </h3>
                    <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                      {scenario.desc}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        ) : (
          <>
            {/* 标签切换 */}
            <Reveal className="mt-10">
              <div
                role="tablist"
                aria-label="应用场景"
                className="inline-flex items-center gap-1 p-1 rounded-full bg-background-50 border border-background-200"
              >
                {scenarios.map((scenario, idx) => (
                  <button
                    key={scenario.id}
                    type="button"
                    role="tab"
                    aria-selected={active === idx}
                    onClick={() => setActive(idx)}
                    className={`px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                      active === idx
                        ? `${a.solid} text-foreground-950`
                        : 'text-foreground-600 hover:text-foreground-950'
                    }`}
                  >
                    {scenario.title}
                  </button>
                ))}
              </div>
            </Reveal>

            <Reveal className="mt-8">
              {scenarios.map((scenario, idx) => (
                <div
                  key={scenario.id}
                  role="tabpanel"
                  hidden={active !== idx}
                  className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center"
                >
                  <div className="relative rounded-lg overflow-hidden">
                    <img
                      src={scenario.image}
                      alt={scenario.alt}
                      title={scenario.alt}
                      className="w-full h-[240px] md:h-[340px] object-cover"
                    />
                  </div>
                  <div>
                    <div className={`w-12 h-12 flex items-center justify-center rounded-md ${a.iconBox}`}>
                      <i className={`${scenario.icon} text-2xl`}></i>
                    </div>
                    <h3 className="mt-4 font-heading font-bold text-foreground-950 text-2xl leading-snug">
                      {scenario.title}
                    </h3>
                    <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                      {scenario.desc}
                    </p>
                  </div>
                </div>
              ))}
            </Reveal>
          </>
        )}
      </div>
    </section>
  );
}